import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Typography } from '../../components/ui/Typography';
import axios from 'axios';
import { 
  RefreshCw, 
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

interface AIAnalyticsRecord {
  id: string;
  created_at: string;
  model_name: string;
  model_provider: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  processing_time_ms: number;
  title_length: number;
  description_length: number;
  keywords_count: number;
  quality_score: number;
  fallback_used: boolean;
  custom_instruction_used: boolean;
  custom_instruction_value: string | null;
  user_id: string | null;
  image_url: string;
  request_source: string;
}

interface AIAnalyticsStats {
  total_requests: number;
  total_cost: number;
  avg_processing_time: number;
  fallback_usage_rate: number;
  custom_instruction_rate: number;
  avg_quality_score: number;
  api_percentage?: number;
  webapp_percentage?: number;
}

interface FilterOptions {
  dateRange: 'today' | '7days' | '30days' | 'all';
  modelName: string;
  customInstructionUsed: 'all' | 'true' | 'false';
  fallbackUsed: 'all' | 'true' | 'false';
  requestSource: string;
  minCost: string;
  maxCost: string;
  search: string;
}

const AIAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AIAnalyticsRecord[]>([]);
  const [stats, setStats] = useState<AIAnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: '7days',
    modelName: 'all',
    customInstructionUsed: 'all',
    fallbackUsed: 'all',
    requestSource: 'all',
    minCost: '',
    maxCost: '',
    search: ''
  });

  const [selectedRecord, setSelectedRecord] = useState<AIAnalyticsRecord | null>(null);
  const [selectedTab, setSelectedTab] = useState<'general' | 'metadata'>('general');
  const [metadata, setMetadata] = useState<any | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<{ name: string; email: string } | null>(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);

  // Trim image URL to remove query parameters for main app URLs
  const trimImageUrl = (url: string): string => {
    if (!url) return url;
    
    // Check if URL contains _large.webp (main app pattern)
    const largeWebpIndex = url.indexOf('_large.webp');
    if (largeWebpIndex !== -1) {
      // Cut everything after _large.webp (including the extension)
      return url.substring(0, largeWebpIndex + '_large.webp'.length);
    }
    
    // Return original URL if not a main app URL
    return url;
  };

  // Get admin token from localStorage
  const getAdminToken = () => {
    const adminAuth = localStorage.getItem('admin-auth');
    if (adminAuth) {
      try {
        const parsed = JSON.parse(adminAuth);
        return parsed.state?.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Fetch user info using existing /admin/users/:id endpoint
  const fetchUserInfo = async (userId: string) => {
    setUserInfoLoading(true);
    try {
      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/admin/users/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const userData = response.data.data.user; // Response structure: { data: { user: {...} } }
        setUserInfo({
          name: userData.name || 'N/A',
          email: userData.email || 'N/A'
        });
      } else {
        setUserInfo(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch user info:', err);
      setUserInfo(null);
    } finally {
      setUserInfoLoading(false);
    }
  };

  // Fetch metadata for a specific analytics record (when user clicks Metadata tab)
  const fetchMetadata = async (analyticsId: string) => {
    setMetadataLoading(true);
    try {
      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/admin/ai-analytics/${analyticsId}/metadata`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMetadata(response.data.data);
      } else {
        setMetadata(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch metadata:', err);
      setMetadata(null);
    } finally {
      setMetadataLoading(false);
    }
  };

  // Fetch analytics data with pagination and filters
  const fetchAnalytics = async (page: number = 1, size: number = 50) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', size.toString());
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, String(value));
      });

      const response = await axios.get(
        `${API_BASE_URL}/admin/ai-analytics?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Backend returns { data: [...], pagination: {...} }
      const responseData = response.data.data || response.data;
      const analyticsData = Array.isArray(responseData) ? responseData : (responseData.data || []);
      const paginationData = responseData.pagination || response.data.pagination || {};
      
      setData(analyticsData);
      setTotalPages(paginationData.totalPages || 1);
      setTotalRecords(paginationData.totalRecords || 0);
      setCurrentPage(paginationData.currentPage || page);
      
    } catch (err) {
      console.error('Failed to fetch AI analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics stats
  const fetchStats = async () => {
    try {
      const token = getAdminToken();
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        params.append(key, String(value));
      });
      const response = await axios.get(
        `${API_BASE_URL}/admin/ai-analytics/stats?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStats(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics(1, pageSize);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize]);

  // Fetch user info when modal is opened with a record that has user_id
  useEffect(() => {
    if (selectedRecord && selectedRecord.user_id && !userInfo) {
      fetchUserInfo(selectedRecord.user_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchAnalytics(page, pageSize);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      dateRange: '7days',
      modelName: 'all',
      customInstructionUsed: 'all',
      fallbackUsed: 'all',
      requestSource: 'all',
      minCost: '',
      maxCost: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 6
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (condition: boolean, trueLabel: string, falseLabel: string) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        condition 
          ? 'bg-yellow-100 text-yellow-800' 
          : 'bg-green-100 text-green-800'
      }`}>
        {condition ? trueLabel : falseLabel}
      </span>
    );
  };

  const renderPagination = () => {
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            variant="outline"
          >
            Previous
          </Button>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            variant="outline"
          >
            Next
          </Button>
        </div>
        
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, totalRecords)}
              </span>{' '}
              of <span className="font-medium">{totalRecords.toLocaleString()}</span> results
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>
            
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <Button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                variant="outline"
                className="rounded-l-md"
              >
                First
              </Button>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const page = startPage + i;
                return (
                  <Button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    variant={currentPage === page ? "default" : "outline"}
                    className="px-3 py-1"
                  >
                    {page}
                  </Button>
                );
              })}
              
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                variant="outline"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                variant="outline"
                className="rounded-r-md"
              >
                Last
              </Button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="h1" className="text-2xl font-bold text-gray-900">
            AI Analytics
          </Typography>
          <Typography variant="small" className="text-gray-600 mt-1">
            Monitor AI engine performance, costs, and usage patterns
          </Typography>
        </div>
        
        <div className="flex items-center">
          <Button
            onClick={() => fetchAnalytics(currentPage, pageSize)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Total Requests</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {stats.total_requests.toLocaleString()}
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Total Cost</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {formatCurrency(stats.total_cost)}
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Avg Processing</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {Math.round(stats.avg_processing_time)}ms
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Fallback Rate</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {(stats.fallback_usage_rate * 100).toFixed(1)}%
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Custom Instructions</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {(stats.custom_instruction_rate * 100).toFixed(1)}%
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Avg Quality</Typography>
            <Typography variant="h3" className="text-2xl font-bold text-gray-900">
              {stats.avg_quality_score.toFixed(1)}
            </Typography>
          </Card>

          <Card className="p-4">
            <Typography variant="small" className="text-gray-600">Request Source</Typography>
            <div className="flex gap-3 mt-1">
              <div className="text-center">
                <Typography variant="small" className="text-xs text-gray-500">Main App</Typography>
                <Typography variant="h3" className="text-xl font-bold text-green-700">
                  {stats.webapp_percentage?.toFixed(1) || '0.0'}%
                </Typography>
              </div>
              <div className="text-center">
                <Typography variant="small" className="text-xs text-gray-500">API</Typography>
                <Typography variant="h3" className="text-xl font-bold text-blue-700">
                  {stats.api_percentage?.toFixed(1) || '0.0'}%
                </Typography>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters Panel */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Request Source</label>
            <select
              value={filters.requestSource}
              onChange={(e) => handleFilterChange('requestSource', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Sources</option>
              <option value="api">API (3rd Party)</option>
              <option value="webapp">Main App</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Instructions</label>
            <select
              value={filters.customInstructionUsed}
              onChange={(e) => handleFilterChange('customInstructionUsed', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="true">With Custom</option>
              <option value="false">Without Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fallback</label>
            <select
              value={filters.fallbackUsed}
              onChange={(e) => handleFilterChange('fallbackUsed', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="true">Fallback</option>
              <option value="false">Primary</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="User ID, Image URL..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex items-center justify-end mt-4">
          <Button onClick={resetFilters} variant="outline" size="sm">
            Reset Filters
          </Button>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="p-6">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <Typography variant="p">{error}</Typography>
          </div>
        </Card>
      )}

      {/* Data Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metadata
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(record.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{record.model_name}</div>
                    <div className="text-sm text-gray-500">{record.model_provider}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>In: {record.input_tokens.toLocaleString()}</div>
                    <div>Out: {record.output_tokens.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(record.estimated_cost_usd)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.processing_time_ms}ms
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>T: {record.title_length}</div>
                    <div>D: {record.description_length}</div>
                    <div>K: {record.keywords_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      record.request_source === 'api' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {record.request_source || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap space-y-1">
                    {getStatusBadge(record.fallback_used, 'Fallback', 'Primary')}
                    {record.custom_instruction_used && (
                      <div>
                        {getStatusBadge(record.custom_instruction_used, 'Custom', 'Standard')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <Button
                      onClick={() => setSelectedRecord(record)}
                      variant="outline"
                      size="sm"
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}
      </Card>

      {/* Record Details Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Typography variant="h2" className="text-xl font-bold">
                  Analytics Record Details
                </Typography>
                <Button
                  onClick={() => {
                    setSelectedRecord(null);
                    setSelectedTab('general');
                    setMetadata(null);
                    setUserInfo(null);
                  }}
                  variant="outline"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  className={`px-6 py-3 font-medium text-sm ${
                    selectedTab === 'general'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setSelectedTab('general')}
                >
                  General
                </button>
                <button
                  className={`px-6 py-3 font-medium text-sm ${
                    selectedTab === 'metadata'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => {
                    setSelectedTab('metadata');
                    if (!metadata) {
                      fetchMetadata(selectedRecord.id);
                    }
                  }}
                >
                  Metadata
                </button>
              </div>

              {/* General Tab Content */}
              {selectedTab === 'general' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Typography variant="small" className="font-medium text-gray-700">Timestamp</Typography>
                      <Typography variant="p">{formatDate(selectedRecord.created_at)}</Typography>
                    </div>
                  
                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Model Information</Typography>
                    <Typography variant="p">{selectedRecord.model_name}</Typography>
                    <Typography variant="small" className="text-gray-600">{selectedRecord.model_provider}</Typography>
                  </div>

                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Token Usage</Typography>
                    <Typography variant="p">Input: {selectedRecord.input_tokens.toLocaleString()}</Typography>
                    <Typography variant="p">Output: {selectedRecord.output_tokens.toLocaleString()}</Typography>
                  </div>

                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Performance</Typography>
                    <Typography variant="p">Cost: {formatCurrency(selectedRecord.estimated_cost_usd)}</Typography>
                    <Typography variant="p">Processing Time: {selectedRecord.processing_time_ms}ms</Typography>
                    <Typography variant="p">Quality Score: {selectedRecord.quality_score}</Typography>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Metadata Lengths</Typography>
                    <Typography variant="p">Title: {selectedRecord.title_length} chars</Typography>
                    <Typography variant="p">Description: {selectedRecord.description_length} chars</Typography>
                    <Typography variant="p">Keywords: {selectedRecord.keywords_count} items</Typography>
                  </div>

                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Status</Typography>
                    <div className="space-y-2">
                      {getStatusBadge(selectedRecord.fallback_used, 'Fallback Used', 'Primary Model')}
                      {getStatusBadge(selectedRecord.custom_instruction_used, 'Custom Instruction', 'Standard Prompt')}
                    </div>
                  </div>

                  {selectedRecord.custom_instruction_value && (
                    <div>
                      <Typography variant="small" className="font-medium text-gray-700">Custom Instruction</Typography>
                      <div className="bg-gray-100 p-3 rounded-md">
                        <Typography variant="small" className="font-mono text-sm">
                          {selectedRecord.custom_instruction_value}
                        </Typography>
                      </div>
                    </div>
                  )}

                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Request Information</Typography>
                    <Typography variant="p">Source: {selectedRecord.request_source}</Typography>
                    {selectedRecord.user_id && (
                      <div className="mt-2">
                        {userInfoLoading ? (
                          <Typography variant="small" className="text-gray-500">Loading user info...</Typography>
                        ) : userInfo ? (
                          <>
                            <Typography variant="p">User: {userInfo.name}</Typography>
                            <Typography variant="small" className="text-gray-600">{userInfo.email}</Typography>
                          </>
                        ) : (
                          <Typography variant="small" className="text-gray-500">User ID: {selectedRecord.user_id}</Typography>
                        )}
                      </div>
                    )}
                    {!selectedRecord.user_id && (
                      <Typography variant="p">User: Anonymous</Typography>
                    )}
                  </div>

                  <div>
                    <Typography variant="small" className="font-medium text-gray-700">Image URL</Typography>
                    <Typography variant="small" className="break-all text-blue-600">
                      <a href={selectedRecord.image_url} target="_blank" rel="noopener noreferrer">
                        {trimImageUrl(selectedRecord.image_url)}
                      </a>
                    </Typography>
                  </div>
                </div>
              </div>
              )}

              {/* Metadata Tab Content */}
              {selectedTab === 'metadata' && (
                <div>
                  {metadataLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                      <Typography variant="p" className="ml-3">Loading metadata...</Typography>
                    </div>
                  ) : metadata ? (
                    <div className="space-y-6">
                      <div>
                        <Typography variant="small" className="font-medium text-gray-700 mb-2">Source</Typography>
                        <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                          metadata.source === 'api' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {metadata.source === 'api' ? '3rd Party API' : 'Main App'}
                        </span>
                      </div>

                      <div>
                        <Typography variant="small" className="font-medium text-gray-700 mb-2">Title</Typography>
                        <div className="bg-gray-50 p-4 rounded-md">
                          <Typography variant="p">{metadata.metadata?.title || 'N/A'}</Typography>
                        </div>
                      </div>

                      <div>
                        <Typography variant="small" className="font-medium text-gray-700 mb-2">Description</Typography>
                        <div className="bg-gray-50 p-4 rounded-md">
                          <Typography variant="p">{metadata.metadata?.description || 'N/A'}</Typography>
                        </div>
                      </div>

                      <div>
                        <Typography variant="small" className="font-medium text-gray-700 mb-2">
                          Keywords ({metadata.metadata?.keywords?.length || 0})
                        </Typography>
                        <div className="bg-gray-50 p-4 rounded-md">
                          {metadata.metadata?.keywords && metadata.metadata.keywords.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {metadata.metadata.keywords.map((keyword: string, index: number) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                >
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <Typography variant="small" className="text-gray-500">No keywords</Typography>
                          )}
                        </div>
                      </div>

                      {metadata.metadata?.created_at && (
                        <div>
                          <Typography variant="small" className="font-medium text-gray-700 mb-2">Created At</Typography>
                          <Typography variant="p">{formatDate(metadata.metadata.created_at)}</Typography>
                        </div>
                      )}

                      {metadata.metadata?.updated_at && (
                        <div>
                          <Typography variant="small" className="font-medium text-gray-700 mb-2">Updated At</Typography>
                          <Typography variant="p">{formatDate(metadata.metadata.updated_at)}</Typography>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-12">
                      <Typography variant="p" className="text-gray-500">No metadata available</Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalyticsPage;
